import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { useAuthStore } from "@/stores/authStore";
import { publicApi } from "@/services/api";
import { useEffect, useState, type ReactNode } from "react";

// Layout
import { AdminLayout, ClientLayout } from "@/components/layout";

// Auth Pages
import { LoginPage } from "@/pages/auth";

// Admin Pages
import {
  DashboardPage,
  ClientsPage,
  ClientFormPage,
  ProductsPage,
  ProductFormPage,
  BudgetsPage,
  BudgetFormPage,
  BudgetViewPage,
  SettingsPage,
  UsersPage,
  UserFormPage,
  UserViewPage,
  ProfilePage,
} from "@/pages/admin";

// Client Pages
import { ClientBudgetsPage, ClientBudgetViewPage } from "@/pages/client";

// Public Pages
import { PublicBudgetPage, UnauthorizedDomainPage } from "@/pages/public";

// Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route Component (Admin only)
function ProtectedRoute() {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Clientes não podem acessar rotas admin
  if (user?.role === "client") {
    return <Navigate to="/client" replace />;
  }

  return (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  );
}

function ClientRoute() {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== "client") {
    return <Navigate to="/admin" replace />;
  }

  return (
    <ClientLayout>
      <Outlet />
    </ClientLayout>
  );
}

function DomainGuard({ children }: { children: ReactNode }) {
  const [allowed, setAllowed] = useState<boolean | null>(null);

  useEffect(() => {
    const hostname = window.location.hostname;
    if (["localhost", "127.0.0.1"].includes(hostname)) {
      setAllowed(true);
      return;
    }

    publicApi
      .validateDomain(hostname)
      .then((response) => {
        setAllowed(Boolean(response.data?.allowed));
      })
      .catch(() => setAllowed(false));
  }, []);

  if (allowed === null) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!allowed) {
    return <UnauthorizedDomainPage />;
  }

  return <>{children}</>;
}

// Redirect based on user role
function RoleBasedRedirect() {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role === "client") {
    return <Navigate to="/client" replace />;
  }

  return <Navigate to="/admin" replace />;
}

// Guest Route Component (redirect if already logged in)
function GuestRoute() {
  const { isAuthenticated, user } = useAuthStore();

  if (isAuthenticated) {
    // Redirecionar baseado no role do usuário
    if (user?.role === "client") {
      return <Navigate to="/client" replace />;
    }
    return <Navigate to="/admin" replace />;
  }

  return <Outlet />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <DomainGuard>
          <Routes>
            {/* Public Budget View (no auth required) */}
            <Route path="/p/:token" element={<PublicBudgetPage />} />

            <Route path="/unauthorized" element={<UnauthorizedDomainPage />} />

            {/* Guest Routes (login) */}
            <Route element={<GuestRoute />}>
              <Route path="/login" element={<LoginPage />} />
            </Route>

            {/* Protected Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute />}>
              <Route index element={<DashboardPage />} />

              {/* Clients */}
              <Route path="clients" element={<ClientsPage />} />
              <Route path="clients/new" element={<ClientFormPage />} />
              <Route path="clients/:id" element={<ClientFormPage />} />
              <Route path="clients/:id/edit" element={<ClientFormPage />} />

              {/* Products */}
              <Route path="products" element={<ProductsPage />} />
              <Route path="products/new" element={<ProductFormPage />} />
              <Route path="products/:id/edit" element={<ProductFormPage />} />

              {/* Budgets */}
              <Route path="budgets" element={<BudgetsPage />} />
              <Route path="budgets/new" element={<BudgetFormPage />} />
              <Route path="budgets/:id" element={<BudgetViewPage />} />
              <Route path="budgets/:id/edit" element={<BudgetFormPage />} />

              {/* Users */}
              <Route path="users" element={<UsersPage />} />
              <Route path="users/new" element={<UserFormPage />} />
              <Route path="users/:id" element={<UserViewPage />} />
              <Route path="users/:id/edit" element={<UserFormPage />} />

              {/* Profile */}
              <Route path="profile" element={<ProfilePage />} />

              {/* Settings */}
              <Route path="settings" element={<SettingsPage />} />
            </Route>

            {/* Protected Client Routes */}
            <Route path="/client" element={<ClientRoute />}>
              <Route index element={<ClientBudgetsPage />} />
              <Route path="budgets/:id" element={<ClientBudgetViewPage />} />
            </Route>

            {/* Redirect root based on user role */}
            <Route path="/" element={<RoleBasedRedirect />} />

            {/* 404 - Redirect based on user role */}
            <Route path="*" element={<RoleBasedRedirect />} />
          </Routes>
        </DomainGuard>
      </BrowserRouter>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "rgba(26, 15, 46, 0.95)",
            color: "#fff",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "12px",
            backdropFilter: "blur(10px)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}
